import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../lib/api'
import { keys } from '../lib/queries'
import { Button, Field, Input, cx } from '../components/ui'

/**
 * The shell every signed-out screen sits in.
 *
 * The wordmark uses the same monospace as task keys, because that face is the
 * app's identity: the first thing you see is the typography you will be
 * reading all day.
 */
function AuthShell({ title, subtitle, children }: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-[340px]">
        <div className="mb-7 flex items-center gap-2">
          <span aria-hidden className="flex h-7 w-7 items-center justify-center rounded-md bg-[var(--accent)]">
            <span className="flex items-end gap-[2px]">
              <i className="block h-[13px] w-[3px] rounded-[1px] bg-white/55" />
              <i className="block h-[9px] w-[3px] rounded-[1px] bg-white" />
              <i className="block h-[6px] w-[3px] rounded-[1px] bg-white/75" />
            </span>
          </span>
          <span className="task-key text-[15px] font-semibold tracking-tight">
            artsol<span className="text-[var(--ink-3)]">/</span>tasks
          </span>
        </div>

        <h1 className="text-[19px] font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-[13px] text-[var(--ink-2)]">{subtitle}</p>}

        <div className="mt-6">{children}</div>
      </div>
    </div>
  )
}

/** Pulls the message and per-field errors out of whatever the API returned. */
function useFormError() {
  const [error, setError] = useState<ApiError | null>(null)
  return {
    error,
    setError,
    field: (name: string) => error?.fields?.[name],
    // A message is only worth showing at the top when it is not already
    // attached to a specific input.
    banner: error && Object.keys(error.fields ?? {}).length === 0 ? error.message : null,
    capture: (e: unknown) => setError(e instanceof ApiError ? e : new ApiError(0, String(e))),
  }
}

function Banner({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 rounded-md border border-[var(--danger)] bg-[color-mix(in_srgb,var(--danger)_8%,transparent)] px-3 py-2 text-[13px] text-[var(--danger)]">
      {children}
    </div>
  )
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 rounded-md border border-[var(--line)] bg-[var(--sunken)] px-3 py-2 text-[13px] text-[var(--ink-2)]">
      {children}
    </div>
  )
}

// ------------------------------------------------------------------ login

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const form = useFormError()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    form.setError(null)
    try {
      await api.login(email, password)
      await queryClient.invalidateQueries({ queryKey: keys.me() })
      navigate(params.get('next') ?? '/', { replace: true })
    } catch (err) {
      form.capture(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell title="Sign in" subtitle="Accounts here are invite-only.">
      {form.banner && <Banner>{form.banner}</Banner>}
      {params.get('reset') === 'done' && (
        <Notice>Your password was changed. Sign in with the new one.</Notice>
      )}

      <form onSubmit={submit} className="space-y-3.5">
        <Field label="Email" htmlFor="email" error={form.field('email')}>
          <Input
            id="email" type="email" autoComplete="username" required autoFocus
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@artsol.dev"
          />
        </Field>

        <Field label="Password" htmlFor="password" error={form.field('password')}>
          <Input
            id="password" type="password" autoComplete="current-password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
          Sign in
        </Button>
      </form>

      <p className="mt-4 text-center text-[12px]">
        <Link to="/forgot" className="text-[var(--ink-3)] hover:text-[var(--ink)]">
          Forgot your password?
        </Link>
      </p>
    </AuthShell>
  )
}

// --------------------------------------------------------- password reset

export function ForgotPage() {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const form = useFormError()

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.requestReset(email)
      setSent(true)
    } catch (err) {
      form.capture(err)
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check your email">
        <Notice>
          If {email} has an account, a reset link is on its way. The link works for two hours.
        </Notice>
        <Link to="/login">
          <Button className="w-full">Back to sign in</Button>
        </Link>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Reset your password" subtitle="We will email you a link to set a new one.">
      {form.banner && <Banner>{form.banner}</Banner>}
      <form onSubmit={submit} className="space-y-3.5">
        <Field label="Email" htmlFor="reset-email" error={form.field('email')}>
          <Input
            id="reset-email" type="email" required autoFocus autoComplete="username"
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
          Send reset link
        </Button>
      </form>
      <p className="mt-4 text-center text-[12px]">
        <Link to="/login" className="text-[var(--ink-3)] hover:text-[var(--ink)]">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  )
}

export function ResetPage() {
  const { token = '' } = useParams()
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const form = useFormError()
  const navigate = useNavigate()

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    form.setError(null)
    try {
      await api.confirmReset(token, password)
      navigate('/login?reset=done', { replace: true })
    } catch (err) {
      form.capture(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell title="Choose a new password">
      {form.banner && <Banner>{form.banner}</Banner>}
      {form.field('token') && <Banner>{form.field('token')}</Banner>}

      <form onSubmit={submit} className="space-y-3.5">
        <Field
          label="New password" htmlFor="new-password"
          error={form.field('password')} hint="At least 10 characters."
        >
          <Input
            id="new-password" type="password" required autoFocus autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
          Set password and sign in
        </Button>
      </form>
    </AuthShell>
  )
}

// ------------------------------------------------------------ invitations

export function AcceptInvitePage() {
  const { token = '' } = useParams()
  const [invite, setInvite] = useState<{ email: string; name: string } | null>(null)
  const [expired, setExpired] = useState(false)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const form = useFormError()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  useEffect(() => {
    api
      .invitePreview(token)
      .then((data) => {
        setInvite(data)
        setName(data.name)
      })
      .catch(() => setExpired(true))
  }, [token])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    form.setError(null)
    try {
      await api.acceptInvite(token, name, password)
      await queryClient.invalidateQueries({ queryKey: keys.me() })
      navigate('/', { replace: true })
    } catch (err) {
      form.capture(err)
    } finally {
      setBusy(false)
    }
  }

  if (expired) {
    return (
      <AuthShell title="This invitation is no longer valid">
        <Notice>
          It expired or was already used. Ask an administrator to send you a new one.
        </Notice>
        <Link to="/login">
          <Button className="w-full">Go to sign in</Button>
        </Link>
      </AuthShell>
    )
  }

  if (!invite) {
    return <AuthShell title="Checking your invitation…">{null}</AuthShell>
  }

  return (
    <AuthShell title="Set up your account" subtitle={`Joining as ${invite.email}`}>
      {form.banner && <Banner>{form.banner}</Banner>}
      {form.field('token') && <Banner>{form.field('token')}</Banner>}

      <form onSubmit={submit} className="space-y-3.5">
        <Field label="Your name" htmlFor="invite-name" error={form.field('name')}>
          <Input
            id="invite-name" required autoFocus value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Anna Petrova"
          />
        </Field>

        <Field
          label="Password" htmlFor="invite-password"
          error={form.field('password')} hint="At least 10 characters."
        >
          <Input
            id="invite-password" type="password" required autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
          Create account
        </Button>
      </form>

      <p className={cx('mt-4 text-center text-[12px] text-[var(--ink-3)]')}>
        You will be signed in straight away.
      </p>
    </AuthShell>
  )
}
